import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Header from "../Header";
import {
  HomeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClipboardDocumentCheckIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  RectangleGroupIcon,
  ChartPieIcon,
  DocumentArrowDownIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  CalculatorIcon,
  UserMinusIcon,
  ArrowRightCircleIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  ClockIcon,
  CalendarDaysIcon,
  NoSymbolIcon,
  XMarkIcon,
  EllipsisHorizontalIcon,
  ArrowRightOnRectangleIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeIconSolid,
  ClipboardDocumentCheckIcon as ClipboardSolid,
  CurrencyDollarIcon as CurrencySolid,
  FolderIcon as FolderSolid,
  EllipsisHorizontalIcon as EllipsisSolid,
} from "@heroicons/react/24/solid";
import { useMsal } from "@azure/msal-react";
import { useData } from "../../providers/DataProvider";
import { RegionProps } from "../../providers/types";

interface LayoutProps {
  children: ReactNode;
}

// ─────────────────────────────────────────────────────────────────────────────
// Desktop sidebar style helpers 
// ─────────────────────────────────────────────────────────────────────────────

const navLinkClass = (active: boolean) =>
  `flex items-center p-2 text-base font-normal rounded-lg group transition-colors ${
    active
      ? "bg-primary-100 text-primary-700 border-l-4 border-primary-500"
      : "text-gray-900 hover:bg-gray-100"
  }`;

const navIconClass = (active: boolean) =>
  `w-6 h-6 flex-shrink-0 transition duration-75 ${
    active ? "text-primary-600" : "text-gray-500 group-hover:text-gray-900"
  }`;

const subMenuBtnClass = (active: boolean) =>
  `flex items-center justify-between w-full p-2 text-sm font-normal rounded-lg group transition-colors ${
    active
      ? "bg-primary-50 text-primary-700"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  }`;

const subLinkClass = (active: boolean) =>
  `flex items-center p-2 text-xs font-normal rounded-lg transition-colors ${
    active
      ? "bg-primary-100 text-primary-700 font-semibold"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  }`;

const subIconColor = (active: boolean) =>
  active ? "text-primary-600" : "text-gray-500";

// ─────────────────────────────────────────────────────────────────────────────
// Desktop NavContent — identical to original
// ─────────────────────────────────────────────────────────────────────────────

interface NavContentProps {
  bonusType: string;
  hasAdminAccess: boolean;
  hasPayrollAccess: boolean;
  hasOpsPlusAccess: boolean;
  isCurrentRoleOpsPlus: boolean;
  hasUploadAccess: boolean;
  isAdminPath: boolean;
  isAdminDashboardPath: boolean;
  isAdminManagePath: boolean;
  isPayrollPath: boolean;
  isPayrollHistoryPath: boolean;
  adminMenuOpen: boolean;
  dashboardMenuOpen: boolean;
  manageMenuOpen: boolean;
  payrollMenuOpen: boolean;
  payrollHistoryMenuOpen: boolean;
  onAdminToggle: () => void;
  onDashboardToggle: () => void;
  onManageToggle: () => void;
  onPayrollToggle: () => void;
  onPayrollHistoryToggle: () => void;
  onNavigate: () => void;
}

const NavContent: React.FC<NavContentProps> = ({
  bonusType, hasAdminAccess, hasPayrollAccess, hasOpsPlusAccess, isCurrentRoleOpsPlus, hasUploadAccess,
  isAdminPath, isAdminDashboardPath, isAdminManagePath, isPayrollPath, isPayrollHistoryPath,
  adminMenuOpen, dashboardMenuOpen, manageMenuOpen, payrollMenuOpen, payrollHistoryMenuOpen,
  onAdminToggle, onDashboardToggle, onManageToggle, onPayrollToggle, onPayrollHistoryToggle, onNavigate,
}) => {
  const showFindProject  = bonusType !== "Stage5Bonus";
  const showFileExplorer = bonusType !== "Stage5Bonus";
  const reviewPath = bonusType === "Stage5Bonus" && isCurrentRoleOpsPlus ? "/review/approvals" : "/review";

  return (
    <ul className="space-y-2">
      <li>
        <NavLink to="/" end className={({ isActive }) => navLinkClass(isActive)} onClick={onNavigate} title="Home">
          {({ isActive }) => (<><HomeIcon className={navIconClass(isActive)} /><span className="ml-3">Home</span></>)}
        </NavLink>
      </li>

      {hasUploadAccess && showFileExplorer && (
        <li>
          <NavLink to="/upload" end className={({ isActive }) => navLinkClass(isActive)} onClick={onNavigate} title="File Explorer">
            {({ isActive }) => (<><FolderIcon className={navIconClass(isActive)} /><span className="ml-3">File Explorer</span></>)}
          </NavLink>
        </li>
      )}

      {showFindProject && (
        <li>
          <NavLink to="/upload/create" end className={({ isActive }) => navLinkClass(isActive)} onClick={onNavigate} title="Find a project">
            {({ isActive }) => (<><MagnifyingGlassIcon className={navIconClass(isActive)} /><span className="ml-3">Find a project</span></>)}
          </NavLink>
        </li>
      )}

      <li>
        <NavLink to={reviewPath} end className={({ isActive }) => navLinkClass(isActive)} onClick={onNavigate} title="Review">
          {({ isActive }) => (<><ClipboardDocumentCheckIcon className={navIconClass(isActive)} /><span className="ml-3">Review</span></>)}
        </NavLink>
      </li>

      {hasAdminAccess && (
        <li>
          <button onClick={onAdminToggle} className={navLinkClass(isAdminPath)} aria-expanded={adminMenuOpen} type="button">
            <div className="flex items-center"><UserGroupIcon className={navIconClass(isAdminPath)} /><span className="ml-3">Admin</span></div>
            {adminMenuOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
          </button>
          {adminMenuOpen && (
            <ul className="mt-2 space-y-2 pl-4">
              <li>
                <button onClick={onDashboardToggle} className={subMenuBtnClass(isAdminDashboardPath)} type="button">
                  <div className="flex items-center"><RectangleGroupIcon className={`w-5 h-5 mr-2 flex-shrink-0 ${subIconColor(isAdminDashboardPath)}`} /><span>Dashboard</span></div>
                  {dashboardMenuOpen ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
                </button>
                {dashboardMenuOpen && (
                  <ul className="mt-2 space-y-1 pl-6">
                    <li><NavLink to="/admin/status-overview" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><ChartPieIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Status Overview</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/bonus-sheets" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><DocumentArrowDownIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Bonus Sheets Download</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/builder-search" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><UserCircleIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Builder Search</span></>)}</NavLink></li>
                  </ul>
                )}
              </li>
              <li>
                <button onClick={onManageToggle} className={subMenuBtnClass(isAdminManagePath)} type="button">
                  <div className="flex items-center"><Cog6ToothIcon className={`w-5 h-5 mr-2 flex-shrink-0 ${subIconColor(isAdminManagePath)}`} /><span>Manage</span></div>
                  {manageMenuOpen ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
                </button>
                {manageMenuOpen && (
                  <ul className="mt-2 space-y-1 pl-6">
                    <li><NavLink to="/admin/busted-jobs" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><ExclamationTriangleIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Busted Jobs</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/calculation-rules" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><CalculatorIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Bonus Calculation Rules</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/ex-employees" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><UserMinusIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Ex-Employee Builders</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/skip-review" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><ArrowRightCircleIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Skip Review</span></>)}</NavLink></li>
                  </ul>
                )}
              </li>
            </ul>
          )}
        </li>
      )}

      {hasPayrollAccess && (
        <li>
          <button onClick={onPayrollToggle} className={navLinkClass(isPayrollPath)} aria-expanded={payrollMenuOpen} type="button">
            <div className="flex items-center"><CurrencyDollarIcon className={navIconClass(isPayrollPath)} /><span className="ml-3">Payroll</span></div>
            {payrollMenuOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
          </button>
          {payrollMenuOpen && (
            <ul className="mt-2 space-y-1 pl-4">
              <li><NavLink to="/payroll" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><BanknotesIcon className={`w-5 h-5 mr-2 ${subIconColor(isActive)}`} /><span>Payroll</span></>)}</NavLink></li>
              {hasOpsPlusAccess && isCurrentRoleOpsPlus && (<li><NavLink to="/admin/payroll/payroll-queue" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><CalendarDaysIcon className={`w-5 h-5 mr-2 ${subIconColor(isActive)}`} /><span>Payroll Queue</span></>)}</NavLink></li>)}
              {hasOpsPlusAccess && isCurrentRoleOpsPlus && (<li><NavLink to="/admin/payroll/payment-exclusions" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><NoSymbolIcon className={`w-5 h-5 mr-2 ${subIconColor(isActive)}`} /><span>Payroll Exclusions</span></>)}</NavLink></li>)}
              <li>
                <button onClick={onPayrollHistoryToggle} className={subMenuBtnClass(isPayrollHistoryPath)} type="button">
                  <div className="flex items-center"><ClockIcon className={`w-5 h-5 mr-2 ${subIconColor(isPayrollHistoryPath)}`} /><span>History</span></div>
                  {payrollHistoryMenuOpen ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
                </button>
                {payrollHistoryMenuOpen && (
                  <ul className="mt-1 space-y-1 pl-6">
                    <li><NavLink to="/admin/payroll/exports" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><DocumentArrowDownIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Export History</span></>)}</NavLink></li>
                    <li><NavLink to="/admin/payroll/imports" end className={({ isActive }) => subLinkClass(isActive)} onClick={onNavigate}>{({ isActive }) => (<><ArrowUpTrayIcon className={`w-4 h-4 mr-2 ${subIconColor(isActive)}`} /><span>Paid History</span></>)}</NavLink></li>
                  </ul>
                )}
              </li>
            </ul>
          )}
        </li>
      )}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Mobile bottom tab bar — Home | Projects | Review | Payroll | More
// ─────────────────────────────────────────────────────────────────────────────

interface BottomTabBarProps {
  hasPayrollAccess: boolean;
  bonusType: string;
  isCurrentRoleOpsPlus: boolean;
  onMoreOpen: () => void;
  isMoreOpen: boolean;
}

const BottomTabBar: React.FC<BottomTabBarProps> = ({
  hasPayrollAccess, bonusType, isCurrentRoleOpsPlus, onMoreOpen, isMoreOpen,
}) => {
  const location = useLocation();
  // Projects tab is shown when bonusType allows it (same logic as sidebar)
  const showProjects = bonusType !== "Stage5Bonus";
  const reviewPath = bonusType === "Stage5Bonus" && isCurrentRoleOpsPlus ? "/review/approvals" : "/review";

  const isHome     = location.pathname === "/";
  // Both /upload and /upload/create are "Projects"
  const isProjects = location.pathname.startsWith("/upload");
  const isReview   = location.pathname === "/review" || location.pathname === "/review/approvals";
  const isPayroll  = location.pathname === "/payroll" || location.pathname.startsWith("/admin/payroll");

  type Tab = {
    id: string; label: string;
    to?: string; end?: boolean;
    icon: React.ReactNode; iconSolid: React.ReactNode;
    isActive: boolean; onTap?: () => void;
  };

  const tabs: Tab[] = [
    {
      id: "home", label: "Home", to: "/", end: true,
      icon: <HomeIcon className="w-[22px] h-[22px]" />,
      iconSolid: <HomeIconSolid className="w-[22px] h-[22px]" />,
      isActive: isHome,
    },
  ];

  if (showProjects) {
    tabs.push({
      // Tab navigates to /upload (File Explorer) — Find a Project is in More sheet
      id: "projects", label: "Projects", to: "/upload", end: false,
      icon: <FolderIcon className="w-[22px] h-[22px]" />,
      iconSolid: <FolderSolid className="w-[22px] h-[22px]" />,
      isActive: isProjects,
    });
  }

  tabs.push({
    id: "review", label: "Review", to: reviewPath, end: true,
    icon: <ClipboardDocumentCheckIcon className="w-[22px] h-[22px]" />,
    iconSolid: <ClipboardSolid className="w-[22px] h-[22px]" />,
    isActive: isReview,
  });

  if (hasPayrollAccess) {
    tabs.push({
      id: "payroll", label: "Payroll", to: "/payroll", end: true,
      icon: <CurrencyDollarIcon className="w-[22px] h-[22px]" />,
      iconSolid: <CurrencySolid className="w-[22px] h-[22px]" />,
      isActive: isPayroll,
    });
  }

  // More is always last
  tabs.push({
    id: "more", label: "More",
    icon: <EllipsisHorizontalIcon className="w-[22px] h-[22px]" />,
    iconSolid: <EllipsisSolid className="w-[22px] h-[22px]" />,
    isActive: isMoreOpen,
    onTap: onMoreOpen,
  });

  const TabInner = ({ tab }: { tab: Tab }) => (
    <span className="relative flex flex-col items-center justify-center gap-[3px] w-full h-full select-none">
      <span className={`absolute top-0 left-1/2 -translate-x-1/2 rounded-full bg-primary-500 transition-all duration-200 ${
        tab.isActive ? "w-7 h-[3px] opacity-100" : "w-0 h-[3px] opacity-0"
      }`} />
      <span className={`transition-all duration-150 ${tab.isActive ? "text-primary-600 -translate-y-px" : "text-gray-400"}`}>
        {tab.isActive ? tab.iconSolid : tab.icon}
      </span>
      <span className={`text-[10px] font-semibold leading-none tracking-wide transition-colors duration-150 ${
        tab.isActive ? "text-primary-600" : "text-gray-400"
      }`}>{tab.label}</span>
    </span>
  );

  return (
    <>
      {/* Spacer to prevent content from hiding behind the fixed bar */}
      <div className="lg:hidden flex-shrink-0"
        style={{ height: "calc(56px + env(safe-area-inset-bottom, 0px))" }}
        aria-hidden="true" />

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/96 backdrop-blur-xl border-t border-gray-200/80"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        aria-label="Mobile navigation"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-primary-300/40 to-transparent" />
        <div className="grid h-14" style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
          {tabs.map((tab) =>
            tab.onTap ? (
              <button key={tab.id} onClick={tab.onTap} type="button"
                className="flex items-stretch active:bg-gray-50/80 transition-colors duration-100"
                aria-label={tab.label} aria-pressed={tab.isActive}>
                <TabInner tab={tab} />
              </button>
            ) : (
              <NavLink key={tab.id} to={tab.to!} end={tab.end}
                className="flex items-stretch active:bg-gray-50/80 transition-colors duration-100"
                aria-label={tab.label}>
                <TabInner tab={tab} />
              </NavLink>
            )
          )}
        </div>
      </nav>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// More bottom sheet
// Inline Region + BonusType + Role selectors + Find a Project + Admin + Payroll management
// ─────────────────────────────────────────────────────────────────────────────

interface MoreSheetProps {
  isOpen: boolean;
  onClose: () => void;
  bonusType: string;
  hasAdminAccess: boolean;
  hasPayrollAccess: boolean;
  hasOpsPlusAccess: boolean;
  isCurrentRoleOpsPlus: boolean;
  hasUploadAccess: boolean;
  role: string | null;
  filteredRoles: string[];
  isRoleLoading: boolean;
  onRoleSelect: (r: string) => Promise<void>;
  // Region — uses full RegionProps objects from DataProvider
  regions: RegionProps[];
  selectedRegion: RegionProps | null;
  isRegionLoading: boolean;
  onRegionChange: (region: RegionProps) => void;
  // BonusType
  isBonusTypeLoading: boolean;
  onBonusTypeChange: (t: string) => void;
  // User
  onSignOut: () => void;
  userDisplayName: string;
}

interface SheetItem { to: string; icon: React.ReactNode; label: string; }

const MoreSheet: React.FC<MoreSheetProps> = ({
  isOpen, onClose,
  bonusType, hasAdminAccess, hasPayrollAccess, hasOpsPlusAccess, isCurrentRoleOpsPlus, hasUploadAccess,
  role, filteredRoles, isRoleLoading, onRoleSelect,
  regions, selectedRegion, isRegionLoading, onRegionChange,
  isBonusTypeLoading, onBonusTypeChange,
  onSignOut, userDisplayName,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const showProjects = bonusType !== "Stage5Bonus";

  useEffect(() => {
    if (!isOpen) return;
    const onPointer = (e: PointerEvent) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Role color accents (cosmetic only — no functional impact)
  const roleAccent: Record<string, string> = {
    Builder: "bg-emerald-500", PM: "bg-sky-500", Ops: "bg-violet-500",
    Uploader: "bg-amber-500", Payroll: "bg-rose-500",
  };

  // Shared pill chip style
  const PillChip = ({
    label, isSelected, onClick, accentBg = "bg-primary-600", loading = false,
  }: { label: string; isSelected: boolean; onClick: () => void; accentBg?: string; loading?: boolean }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
        border-2 transition-all duration-150 active:scale-95 whitespace-nowrap
        ${isSelected
          ? `text-white border-transparent shadow-sm ${accentBg}`
          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
        }
        ${loading ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      {isSelected && !loading && (
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {loading && isSelected && (
        <svg className="animate-spin w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      )}
      {label}
    </button>
  );

  // Admin nav grid items
  const adminItems: SheetItem[] = hasAdminAccess ? [
    { to: "/admin/status-overview",   icon: <ChartPieIcon className="w-5 h-5" />,            label: "Status Overview" },
    { to: "/admin/bonus-sheets",      icon: <DocumentArrowDownIcon className="w-5 h-5" />,   label: "Bonus Sheets" },
    { to: "/admin/builder-search",    icon: <UserCircleIcon className="w-5 h-5" />,          label: "Builder Search" },
    { to: "/admin/busted-jobs",       icon: <ExclamationTriangleIcon className="w-5 h-5" />, label: "Busted Jobs" },
    { to: "/admin/calculation-rules", icon: <CalculatorIcon className="w-5 h-5" />,          label: "Calc Rules" },
    { to: "/admin/ex-employees",      icon: <UserMinusIcon className="w-5 h-5" />,           label: "Ex-Employees" },
    { to: "/admin/skip-review",       icon: <ArrowRightCircleIcon className="w-5 h-5" />,    label: "Skip Review" },
  ] : [];

const payrollItems: SheetItem[] = [];

// Only include these if user has OpsPlus access and is in the current OpsPlus role
if (hasOpsPlusAccess && isCurrentRoleOpsPlus) {
  payrollItems.push(
    { to: "/admin/payroll/payroll-queue", icon: <CalendarDaysIcon className="w-5 h-5" />, label: "Payroll Queue" },
    { to: "/admin/payroll/payment-exclusions", icon: <NoSymbolIcon className="w-5 h-5" />, label: "Exclusions" }
  );
}

// Include these if the user has payroll access
if (hasPayrollAccess) {
  payrollItems.push(
    { to: "/admin/payroll/exports", icon: <DocumentArrowDownIcon className="w-5 h-5" />, label: "Export History" },
    { to: "/admin/payroll/imports", icon: <ArrowUpTrayIcon className="w-5 h-5" />, label: "Paid History" }
  );
}

  const NavGrid = ({ items }: { items: SheetItem[] }) => (
    <div className="grid grid-cols-3 gap-2">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end onClick={onClose}
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border text-center transition-all duration-150 active:scale-95 ${
              isActive ? "bg-primary-50 border-primary-200 text-primary-700" : "bg-gray-50 border-gray-100 text-gray-600 hover:bg-gray-100"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span className={isActive ? "text-primary-500" : "text-gray-400"}>{item.icon}</span>
              <span className="text-[11px] font-semibold leading-tight">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  );

  const BONUS_TYPES = [
    { value: "ClosingBonus", label: "Closing Bonus" },
    { value: "Stage5Bonus",  label: "Stage 5 Bonus" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className={`lg:hidden fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 ${
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`} aria-hidden="true" />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog" aria-modal="true" aria-label="More options"
        className={`lg:hidden fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[28px] shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{
          maxHeight: "88vh",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 4.5rem)",
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2.5 pb-0.5">
          <div className="w-9 h-[5px] rounded-full bg-gray-200" />
        </div>

        {/* Sheet header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 tracking-tight">More</h2>
          <button onClick={onClose} type="button" aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto overscroll-contain px-4 space-y-5 pt-4"
          style={{ maxHeight: "calc(88vh - 90px)" }}>

          {/* ── Settings ──────────────────────────────────────────────────── */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5 px-0.5">
              Settings
            </p>
            <div className="bg-gray-50 rounded-2xl border border-gray-100 divide-y divide-gray-200/80">

              {/* Region chips — derived from DataProvider regions[] + selectedRegion */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Region</p>
                  {isRegionLoading && (
                    <svg className="animate-spin w-3 h-3 text-primary-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {regions.length === 0 ? (
                    <span className="text-xs text-gray-400 italic">Loading regions…</span>
                  ) : (
                    regions.map((r) => (
                      <PillChip
                        key={r.regionId}
                        label={r.regionName}
                        isSelected={selectedRegion?.regionId === r.regionId}
                        onClick={() => onRegionChange(r)}
                        accentBg="bg-blue-600"
                        loading={isRegionLoading}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Bonus Type chips */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <TagIcon className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Bonus Type</p>
                  {isBonusTypeLoading && (
                    <svg className="animate-spin w-3 h-3 text-amber-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {BONUS_TYPES.map((t) => (
                    <PillChip
                      key={t.value}
                      label={t.label}
                      isSelected={bonusType === t.value}
                      onClick={() => onBonusTypeChange(t.value)}
                      accentBg="bg-amber-500"
                      loading={isBonusTypeLoading}
                    />
                  ))}
                </div>
              </div>

              {/* Role chips */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Role</p>
                  {isRoleLoading && (
                    <svg className="animate-spin w-3 h-3 text-primary-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                  )}
                </div>
                {isRoleLoading && filteredRoles.length === 0 ? (
                  <span className="text-xs text-gray-400 italic">Loading roles…</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {filteredRoles.map((r) => (
                      <PillChip
                        key={r}
                        label={r}
                        isSelected={r === role}
                        onClick={() => onRoleSelect(r)}
                        accentBg={roleAccent[r] ?? "bg-primary-600"}
                        loading={isRoleLoading}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* User + Log off */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary-700 select-none">
                        {userDisplayName ? userDisplayName.charAt(0).toUpperCase() : "?"}
                      </span>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{userDisplayName || "Signed in"}</p>
                      <p className="text-[10px] text-gray-400 truncate">Highland Homes</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { onClose(); onSignOut(); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 border border-red-100 transition-all duration-150 flex-shrink-0"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    Log Off
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Projects — Find a Project (second projects page) ────────────── */}
          {showProjects && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5 px-0.5">
                Projects
              </p>
              <div className="grid grid-cols-3 gap-2">
                {hasUploadAccess && (
                  <NavLink to="/upload" end onClick={onClose}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border text-center transition-all duration-150 active:scale-95 ${
                        isActive ? "bg-primary-50 border-primary-200 text-primary-700" : "bg-gray-50 border-gray-100 text-gray-600 hover:bg-gray-100"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? "text-primary-500" : "text-gray-400"}><FolderIcon className="w-5 h-5" /></span>
                        <span className="text-[11px] font-semibold leading-tight">File Explorer</span>
                      </>
                    )}
                  </NavLink>
                )}
                <NavLink to="/upload/create" end onClick={onClose}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border text-center transition-all duration-150 active:scale-95 ${
                      isActive ? "bg-primary-50 border-primary-200 text-primary-700" : "bg-gray-50 border-gray-100 text-gray-600 hover:bg-gray-100"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={isActive ? "text-primary-500" : "text-gray-400"}><MagnifyingGlassIcon className="w-5 h-5" /></span>
                      <span className="text-[11px] font-semibold leading-tight">Find a Project</span>
                    </>
                  )}
                </NavLink>
              </div>
            </div>
          )}

          {/* ── Admin ─────────────────────────────────────────────────────── */}
          {hasAdminAccess && adminItems.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5 px-0.5">Admin</p>
              <NavGrid items={adminItems} />
            </div>
          )}

          {/* ── Payroll management ────────────────────────────────────────── */}
          {hasPayrollAccess && payrollItems.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5 px-0.5">
                Payroll Management
              </p>
              <NavGrid items={payrollItems} />
            </div>
          )}

          {/* ── App branding ──────────────────────────────────────────────── */}
          <div className="flex flex-col items-center gap-0.5 py-4 border-t border-gray-100">
            <p className="text-sm font-bold text-gray-700 tracking-tight">Construction Bonus</p>
            <p className="text-xs text-gray-400">Highland Homes</p>
            <p className="text-[10px] text-gray-300 mt-1">© {new Date().getFullYear()} Highland Homes. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Focus trap (original implementation preserved)
// ─────────────────────────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function useFocusTrap(
  active: boolean,
  containerRef: React.RefObject<HTMLElement>,
  initialFocusRef?: React.RefObject<HTMLElement>
) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    const getFocusable = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && el.offsetParent !== null
      );
    initialFocusRef?.current?.focus();
    if (document.activeElement !== initialFocusRef?.current) getFocusable()[0]?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
    };
    container.addEventListener("keydown", handleKeyDown);
    return () => { container.removeEventListener("keydown", handleKeyDown); previousFocusRef.current?.focus(); };
  }, [active, containerRef, initialFocusRef]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────────────────────────────────────

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();
  const {
    bonusType, setBonusType,
    role, setRole, loadDataForRole, isRoleLoading,
    regions, selectedRegion, updateSelectedRegion, isRegionLoading,
    isBonusTypeLoading,
  } = useData();

  const [adminMenuOpen,          setAdminMenuOpen]          = useState(false);
  const [dashboardMenuOpen,      setDashboardMenuOpen]      = useState(false);
  const [manageMenuOpen,         setManageMenuOpen]         = useState(false);
  const [payrollMenuOpen,        setPayrollMenuOpen]        = useState(false);
  const [payrollHistoryMenuOpen, setPayrollHistoryMenuOpen] = useState(false);
  const [drawerOpen,             setDrawerOpen]             = useState(false);
  const [moreSheetOpen,          setMoreSheetOpen]          = useState(false);

  const drawerRef  = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const currentRole = localStorage.getItem("cba.role") || role || "";


  const userRoles = useMemo<string[]>(
    () => (accounts[0]?.idTokenClaims?.roles as string[] | undefined) ?? [],
    [accounts]
  );
  const hasPayrollAccess = userRoles.includes("Payroll");
  const hasAdminAccess   = userRoles.includes("Admin");
  const hasUploadAccess  = userRoles.includes("Uploader");
  const hasOpsPlusAccess = userRoles.includes("OpsPlus");
  const isCurrentRoleOpsPlus = currentRole === "OpsPlus";
  const canSeeAdminMenu = hasOpsPlusAccess && currentRole === "OpsPlus";
  const canSeePayrollMenu =
      (hasOpsPlusAccess && currentRole === "OpsPlus") ||
      (hasPayrollAccess && currentRole === "Payroll");

  const userDisplayName = useMemo(() => {
    const claims = accounts[0]?.idTokenClaims as Record<string, unknown> | undefined;
    return (claims?.name as string) || (claims?.preferred_username as string) || accounts[0]?.username || "";
  }, [accounts]);

  // Filtered roles for the mobile sheet — same logic as Header
  const filteredRoles = useMemo(() =>
    userRoles
      .filter((r) => {
        if (r === "Admin") return false;
        if (bonusType !== "ClosingBonus" && (r === "Ops" || r === "PM" || r === "Uploader")) return false;
        return true;
      })
      .sort((a, b) => a.localeCompare(b)),
    [userRoles, bonusType]
  );

  // Auto-select first role (mirrors Header logic, harmless if Header already set it)
  useEffect(() => {
    if (role || filteredRoles.length === 0) return;
    const firstRole = filteredRoles[0];
    setRole(firstRole);
    loadDataForRole(firstRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRoles]);

  // Auto-select first region when regions load and none is selected yet
  // DataProvider already does this in loadRegions, but belt-and-suspenders for mobile
  useEffect(() => {
    if (selectedRegion || regions.length === 0) return;
    updateSelectedRegion(regions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, selectedRegion]);

  const handleRoleSelect = useCallback(async (selectedRole: string) => {
    setRole(selectedRole);
    setMoreSheetOpen(false);
    await loadDataForRole(selectedRole);
    navigate("/", { replace: true });
  }, [setRole, loadDataForRole, navigate]);

  const handleSignOut = useCallback(() => {
    instance.logoutRedirect().catch(console.error);
  }, [instance]);

  const handleRegionChange = useCallback((region: RegionProps) => {
    updateSelectedRegion(region); // correctly triggers isRegionLoading + data reload
  }, [updateSelectedRegion]);

  const handleBonusTypeChange = useCallback((t: string) => {
    if (t !== bonusType) setBonusType(t);
  }, [bonusType, setBonusType]);

  // Path flags
  const isAdminPath          = useMemo(() => location.pathname.startsWith("/admin"), [location.pathname]);
  const isAdminDashboardPath = useMemo(() => ["/admin/status-overview", "/admin/bonus-sheets", "/admin/builder-search"].includes(location.pathname), [location.pathname]);
  const isAdminManagePath    = useMemo(() => ["/admin/busted-jobs", "/admin/calculation-rules", "/admin/ex-employees", "/admin/skip-review"].includes(location.pathname), [location.pathname]);
  const isPayrollPath        = useMemo(() => location.pathname === "/payroll" || location.pathname.startsWith("/admin/payroll") || location.pathname === "/admin/ops-excluded-jobs", [location.pathname]);
  const isPayrollHistoryPath = useMemo(() => ["/admin/payroll/exports", "/admin/payroll/imports"].includes(location.pathname), [location.pathname]);

  // Close drawer + sheet on route change
  useEffect(() => { setDrawerOpen(false); setMoreSheetOpen(false); }, [location.pathname]);

  // Escape closes drawer
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  // Body scroll lock
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("overflow-hidden", drawerOpen || moreSheetOpen);
    return () => document.body.classList.remove("overflow-hidden");
  }, [drawerOpen, moreSheetOpen]);

  useFocusTrap(drawerOpen, drawerRef, closeBtnRef);

  // Auto-expand sidebar sections on navigation
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith("/admin")) {
      setAdminMenuOpen(true);
      if (isAdminDashboardPath) setDashboardMenuOpen(true);
      if (isAdminManagePath)    setManageMenuOpen(true);
    }
    if (isPayrollPath) {
      setPayrollMenuOpen(true);
      if (isPayrollHistoryPath) setPayrollHistoryMenuOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleAdminToggle          = useCallback(() => setAdminMenuOpen((v)          => !v), []);
  const handleDashboardToggle      = useCallback(() => setDashboardMenuOpen((v)      => !v), []);
  const handleManageToggle         = useCallback(() => setManageMenuOpen((v)         => !v), []);
  const handlePayrollToggle        = useCallback(() => setPayrollMenuOpen((v)        => !v), []);
  const handlePayrollHistoryToggle = useCallback(() => setPayrollHistoryMenuOpen((v) => !v), []);
  const handleNavigate             = useCallback(() => setDrawerOpen(false), []);

  const navContentProps = useMemo<NavContentProps>(
    () => ({
      bonusType, hasAdminAccess: canSeeAdminMenu, hasPayrollAccess: canSeePayrollMenu, hasOpsPlusAccess, isCurrentRoleOpsPlus, hasUploadAccess,
      isAdminPath, isAdminDashboardPath, isAdminManagePath, isPayrollPath, isPayrollHistoryPath,
      adminMenuOpen, dashboardMenuOpen, manageMenuOpen, payrollMenuOpen, payrollHistoryMenuOpen,
      onAdminToggle: handleAdminToggle, onDashboardToggle: handleDashboardToggle,
      onManageToggle: handleManageToggle, onPayrollToggle: handlePayrollToggle,
      onPayrollHistoryToggle: handlePayrollHistoryToggle, onNavigate: handleNavigate,
    }),
    [
      bonusType, canSeeAdminMenu, canSeePayrollMenu, hasOpsPlusAccess, isCurrentRoleOpsPlus, hasUploadAccess,
      isAdminPath, isAdminDashboardPath, isAdminManagePath, isPayrollPath, isPayrollHistoryPath,
      adminMenuOpen, dashboardMenuOpen, manageMenuOpen, payrollMenuOpen, payrollHistoryMenuOpen,
      handleAdminToggle, handleDashboardToggle, handleManageToggle,
      handlePayrollToggle, handlePayrollHistoryToggle, handleNavigate,
    ]
  );

  return (
    <div className="flex flex-col h-screen antialiased bg-gray-50">
      {/* Header gets onMenuToggle for the desktop sidebar drawer */}
      <Header onMenuToggle={() => setDrawerOpen((prev) => !prev)} />

      <div className="flex flex-1 overflow-hidden">

        {/* Desktop sidebar */}
        <aside
          className="hidden lg:flex fixed top-0 left-0 z-40 w-48 h-screen pt-14 bg-white border-r border-gray-200 flex-col"
          aria-label="Sidenav"
        >
          <div className="overflow-y-auto py-5 px-3 h-full">
            <NavContent {...navContentProps} />
          </div>
        </aside>

        {/* Mobile drawer backdrop */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            aria-hidden="true"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Mobile drawer panel (slides in from left — legacy fallback, bottom bar is primary) */}
        <div
          ref={drawerRef}
          className={`fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-gray-200 pt-14 transform transition-transform duration-300 ease-in-out lg:hidden ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          role="dialog" aria-modal="true" aria-label="Mobile navigation"
        >
          <button
            ref={closeBtnRef}
            onClick={() => setDrawerOpen(false)}
            className="absolute top-3 right-3 p-1 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close menu" type="button"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="overflow-y-auto py-5 px-3 h-full">
            <NavContent {...navContentProps} />
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 lg:ml-48 pt-14 overflow-y-auto transition-all duration-300">
          <div className="h-full">{children}</div>
        </main>
      </div>

      {/* Mobile: bottom tab bar */}
      <BottomTabBar
        hasPayrollAccess={ canSeePayrollMenu } 
        bonusType={bonusType}
        isCurrentRoleOpsPlus={ isCurrentRoleOpsPlus }
        onMoreOpen={() => setMoreSheetOpen((v) => !v)}
        isMoreOpen={moreSheetOpen}
      />

      {/* Mobile: More sheet */}
      <MoreSheet
        isOpen={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        bonusType={bonusType}
        hasAdminAccess={canSeeAdminMenu}
        hasPayrollAccess={canSeePayrollMenu}
        hasOpsPlusAccess={hasOpsPlusAccess}
        isCurrentRoleOpsPlus={isCurrentRoleOpsPlus}
        hasUploadAccess={hasUploadAccess}
        role={role}
        filteredRoles={filteredRoles}
        isRoleLoading={isRoleLoading}
        onRoleSelect={handleRoleSelect}
        regions={regions}
        selectedRegion={selectedRegion}
        isRegionLoading={isRegionLoading}
        onRegionChange={handleRegionChange}
        isBonusTypeLoading={isBonusTypeLoading}
        onBonusTypeChange={handleBonusTypeChange}
        onSignOut={handleSignOut}
        userDisplayName={userDisplayName}
      />
    </div>
  );
};

export default Layout;